import React, { useCallback, useEffect, useState } from 'react';
import Pagination from 'components/Libs/Pagination';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';
import { RaisedButton, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TextField } from 'material-ui';

import RangeDates from './RangeDates';
import AddUpdateModal from './AddUpdateModal';
import TagsModal from './TagsModal';
import LinksTags from './LinksTags';
import styles from 'styles';
import LinksRow from './LinksRow';
import moment from 'moment';

import { getAnalyticsLinksList } from 'actions/analytics/linksList';

function LinksList({ analyticsLinksList, getAnalyticsLinksList }) {
    const rowsPerPage = [20, 50, 100];
    const [rows, setRows] = useState([]);
    const [rowsForPagination, setRowsForPagination] = useState([]);
    const [page, setPage] = useState(1);
    const [numberOfRows, setNumberOfRows] = useState(20);
    const [searchText, setSearchText] = useState('');
    const [tags, setTags] = useState([]);
    const [from, setFrom] = useState(null);
    const [until, setUntil] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentDialogType, setCurrentDialogType] = useState(null);
    const [allTags, setAllTags] = useState([]);

    const changeRowsByPagination = (_page, _numberOfRows, _searchText, _tags, _fromDate, _untilDate) => {
        const elementsOnPage = _numberOfRows;
        const offset = _page - 1;
        const _from = offset * elementsOnPage;
        const _to = (offset * elementsOnPage) + elementsOnPage;

        let analyticsLinksListCopy = _.cloneDeep(analyticsLinksList);

        if (_searchText) {
            const regex = new RegExp(`${_searchText}`, 'i');
            analyticsLinksListCopy = analyticsLinksListCopy.filter(i => regex.test(i.value));
        }

        if (_tags && _tags.length) {
            const tagsValues = _tags.map(_t => _t.value);
            analyticsLinksListCopy = analyticsLinksListCopy.filter((item) => {
                const itemTagsValues = item.tags.map(t => t.value);
                // return _.intersection(tagsValues, itemTagsValues).length; // has any of tags
                return tagsValues.every(t => itemTagsValues.includes(t)); // has all specified tags
            });
        }

        if (_fromDate) {
            analyticsLinksListCopy = analyticsLinksListCopy.filter(item => moment(_fromDate).isSameOrBefore(item.createdAt));
        }

        if (_untilDate) {
            analyticsLinksListCopy = analyticsLinksListCopy.filter(item => moment(_untilDate).isSameOrAfter(item.createdAt));
        }

        setRowsForPagination(analyticsLinksListCopy);

        return analyticsLinksListCopy.slice(_from, _to);
    };

    const handleUpdateRow = ({ page: p, numberOfRows: n }, _searchText, _tags, _fromDate, _untilDate) => {
        setRows(changeRowsByPagination(p, n, _searchText, _tags || tags, _fromDate, _untilDate));
        setPage(p);
        setNumberOfRows(n);
    };

    useEffect(() => {
        handleUpdateRow({ page, numberOfRows }, searchText, tags, from, until);

        const uniqueTags = _.sortBy(_.uniqBy(_.flatten(analyticsLinksList.map(a => a.tags)), 'value'), ['value']);
        setAllTags(uniqueTags);
    }, [analyticsLinksList]);

    const handleSearch = (e) => {
        const _searchText = e.target.value;
        if (_searchText.length < 2) {
            setRows(changeRowsByPagination(1, numberOfRows, '', tags, from, until));
            setSearchText('');
            return;
        }
        setPage(1);

        handleUpdateRow({ page: 1, numberOfRows }, _searchText, tags, from, until);
        setSearchText(_searchText);
    };

    const handleChangeTags = (_tags) => {
        setTags(_tags);
        handleUpdateRow({ page: 1, numberOfRows }, searchText, _tags, from, until);
    };

    const renderPagination = (
        <Pagination
            total={rowsForPagination.length}
            rowsPerPage={rowsPerPage}
            page={page}
            numberOfRows={numberOfRows}
            updateRows={e => handleUpdateRow(e, searchText)}
        />
    );

    const changeFilterByDate = (type, date) => {
        if (type === 'from') {
            setFrom(date);
            handleUpdateRow({ page: 1, numberOfRows }, searchText, tags, date, until);
        } else {
            setUntil(date);
            handleUpdateRow({ page: 1, numberOfRows }, searchText, tags, from, date);
        }
    };

    const handleOpenDialog = (type) => {
        setOpenDialog(true);
        setCurrentDialogType(type);
    };

    const handleModifyLinksList = () => {
        getAnalyticsLinksList();
    };

    const pushToTagsSearch = (tag) => {
        handleChangeTags(_.uniqBy([...tags, tag], 'value'));
    };

    return (
        <div>
            <AddUpdateModal 
                open={currentDialogType === 'add' && openDialog}
                setOpen={setOpenDialog}
                type={currentDialogType}
                handleSearch={handleSearch}
                handleModifyLinksList={handleModifyLinksList}
            />
            <TagsModal 
                open={currentDialogType === 'tags' && openDialog}
                setOpen={setOpenDialog}
                allTags={allTags}
                pushToTagsSearch={pushToTagsSearch}
            />
            <div className='table-header'>
                <div className='item' />
                <div className='item'>
                    {renderPagination}
                </div>
            </div>
            <div className='table-header'>
                <div className='item' style={{ display: 'flex' }}>
                    <TextField
                        defaultValue={searchText}
                        floatingLabelText='Search'
                        onChange={handleSearch}
                        style={{ marginRight: '30px' }}
                    />
                    <RangeDates
                        from={from}
                        until={until}
                        changeDateRange={changeFilterByDate}
                        handleChangeDate={changeFilterByDate}
                    />
                </div>
                <div className='item' style={{ display: 'flex' }}>
                    <div style={{ width: '50%', marginTop: '15px' }}>
                        <LinksTags 
                            tags={tags}
                            setTags={handleChangeTags}
                        />
                    </div>
                    <div style={{ width: '50%' }}>
                        <RaisedButton
                            style={{ margin: '20px 0 0 20px' }}
                            label='Add record'
                            secondary
                            onClick={() => handleOpenDialog('add')}
                        />
                        <RaisedButton
                            style={{ margin: '20px 0 0 20px' }}
                            label='All tags'
                            onClick={() => handleOpenDialog('tags')}
                        />
                    </div>
                </div>
            </div>
            <div className='table-body'>
                {
                    !rows.length ?
                        <div className='nothing-found'>Not found</div>
                        :
                        <Table selectable={false}>
                            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                <TableRow>
                                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnCSS, width: '53%' }}>
                                        Value
                                    </TableHeaderColumn>
                                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '17%' }}>
                                        User Info
                                    </TableHeaderColumn>
                                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '20%' }}>
                                        Tags
                                    </TableHeaderColumn>
                                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '10%' }}>
                                        {null}
                                    </TableHeaderColumn>
                                </TableRow>
                            </TableHeader>

                            <TableBody displayRowCheckbox={false}>
                                {
                                    rows.map(item => (
                                        <LinksRow
                                            key={item._id}
                                            item={item}
                                            handleModifyLinksList={handleModifyLinksList}
                                        />
                                    ))
                                }
                            </TableBody>
                        </Table>
                }
            </div>
        </div>
    );
}

export default connect(
    state => ({
        analyticsLinksList: state.linksList.items || [],
        isLoaded: true,
    }),
    dispatch => bindActionCreators(
        {
            getAnalyticsLinksList,
        },
        dispatch,
    ),
)(LinksList);



// WEBPACK FOOTER //
// ./app/components/Analytics/LinksList.jsx